import {
  GROUP_BADGES,
  GROUP_REQUIRES,
  MANDATORY_ATTRIBUTE_KEYS,
} from '~/constants/config/enrichment-attribute-rules';
import type { GoogleCatalogGroup } from '~/features/content-enrichment/types/google-catalog.types';

import type { EnrichmentAttributeGroup, EnrichmentAttributeOption } from './types';

/**
 * The single adapter between the google agent's curated taxonomy and the panel's own
 * shape. Replaces the old config-service adapter for this panel — only its two
 * generic helpers (`toGroupSlug`, `labelForKey`) survive, in `panel-attribute-helpers.ts`.
 *
 * Two things the config version had to invent are now on the wire:
 *
 * - **Identity.** Group `id` is a stable string (`CONTENT`, `SEARCH`), so it is used as
 *   the slug directly. The config version slugged the display NAME because config
 *   returned per-environment UUIDs, which meant a rename silently broke every rule
 *   keyed off it (and did — see migration 0007).
 * - **Dependencies.** `requires` comes from the payload instead of the client's
 *   `ATTR_REQUIRES` map.
 *
 * What stays client-side is product policy the catalog does not describe: gating,
 * badges, and which attributes cannot be switched off.
 *
 * Defaults follow one rule: an attribute starts **off** if its group is gated or if it
 * is a post-enrichment step, and **on** otherwise. Turning a step on is a run-cost
 * decision, so it is always opt-in. Mandatory attributes always start on.
 *
 * Groups keep server order. Groups the server returned empty are omitted: a `0/0` card
 * is not actionable.
 */
export function adaptGoogleCatalog(
  groups: readonly GoogleCatalogGroup[],
): EnrichmentAttributeGroup[] {
  const out: EnrichmentAttributeGroup[] = [];

  for (const group of groups) {
    // Only offer what the agent says is toggleable — a control over a non-selectable
    // entry would accept input and change nothing.
    const selectable = group.attributes.filter((attr) => attr.selectable);
    if (selectable.length === 0) continue;

    const groupRequires = GROUP_REQUIRES[group.id];
    const badge = GROUP_BADGES[group.id];
    const isGated = groupRequires !== undefined;

    const attributes: EnrichmentAttributeOption[] = selectable.map((attr) => {
      const mandatory = MANDATORY_ATTRIBUTE_KEYS.has(attr.key);
      // The wire carries a list; the panel models a single dependency, so only the
      // first is honoured. Every catalog entry currently declares at most one (only
      // phrase_descriptions declares any), but nothing enforces that — a future entry
      // with two dependencies would have the second silently ignored here, and
      // widening the panel's model is the fix if that day comes.
      const requiresKey = attr.requires[0];
      return {
        key: attr.key,
        label: attr.label,
        ...(requiresKey !== undefined ? { requiresKey } : {}),
        ...(mandatory ? { mandatory: true } : {}),
        defaultSelected: mandatory || (!isGated && attr.generation !== 'post_step'),
      };
    });

    // The heading is `name` (e.g. "Content"); `label` is the parenthesised subtitle
    // (e.g. "Generated Copy") — omitted when it duplicates `name`, which is exactly
    // the SEARCH group's case (config never gave it a description), so it renders as
    // a bare "Search Attributes" with no parenthetical, matching the original panel.
    out.push({
      slug: group.id,
      label: group.name,
      ...(group.label !== group.name ? { subLabel: group.label } : {}),
      ...(badge !== undefined ? { badge } : {}),
      ...(groupRequires !== undefined ? { requiresKey: groupRequires } : {}),
      attributes,
    });
  }

  return out;
}
